import React, { useMemo, useRef, useState } from "react";
import HelpModal from "../components/HelpModal";
import { useFamilyData } from "../hooks/useFamilyData";
import { isTaskDueSoon, isTaskOverdue } from "../utils/time";
import appPackage from "../../package.json";

export default function NavBar({
  familyId,
  families = [],
  activeFamilyId,
  setActiveFamilyId,
  familySettings,
  isKiosk,
  setIsKiosk,
  setView,
  pinInput,
  setPinInput,
  logout,
  onStartTour,
  onStartAdvancedTour,
  onOpenFeedback,
  isAdmin,
  canManage = false,
  isKidAccount = false,
  feedbackUnreadCount = 0,
  broadcastMessage = ""
}) {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [pinError, setPinError] = useState("");
  const pinErrorTimeoutRef = useRef(null);
  const appVersion = appPackage?.version || "2.7.16";
  const { tasks = [] } = useFamilyData(familyId);
  const { dueSoonCount, overdueCount } = useMemo(() => {
    const now = new Date();
    const graceMins = Number(familySettings?.autoFailGraceMins || 0);
    const dueSoon = tasks.filter(
      t => t.status === "open" && isTaskDueSoon(t, 120, now)
    ).length;
    const overdue = tasks.filter(
      t => t.status === "open" && isTaskOverdue(t, now, graceMins)
    ).length;
    return { dueSoonCount: dueSoon, overdueCount: overdue };
  }, [tasks, familySettings?.autoFailGraceMins]);

  return (
    <>
      <nav className={`glass glass-nav nav-bar theme-${familySettings?.theme || 'ocean'}`}>
        <div className="nav-left">
          <div className="family-name">
            {familySettings?.familyName || "Family"}
          </div>
          <div className={`family-tier ${familySettings?.isPro ? "pro" : "free"}`}>
            {familySettings?.isPro ? "PRO FAMILY" : "FREE FAMILY"}
          </div>
          {Array.isArray(families) && families.length > 1 && typeof setActiveFamilyId === "function" && (
            <select
              className="family-switch"
              aria-label="Switch family"
              value={activeFamilyId || ""}
              onChange={(event) => setActiveFamilyId(event.target.value)}
            >
              {families.map((family) => (
                <option key={family.id} value={family.id}>
                  {family.familyName || "Family"}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="nav-center" data-tour="nav-brand">
          <span className="nav-title">FamilySync</span>
          <span className="nav-version">v{appVersion}</span>
        </div>

        <div className="nav-controls nav-right">
          {familySettings?.isPro && (dueSoonCount > 0 || overdueCount > 0) && (
            <div className="due-banner">
              {overdueCount > 0 && <span>Overdue: {overdueCount}</span>}
              {dueSoonCount > 0 && <span>Due soon: {dueSoonCount}</span>}
            </div>
          )}
          {isKiosk ? (
            <>
              <input
                type="password"
                className="pin-input-field glass"
                value={pinInput}
                onChange={e => setPinInput(e.target.value)}
                placeholder="PIN"
              />
              {pinError && (
                <div className="pin-error" role="alert">
                  {pinError}
                </div>
              )}
              <button
                className="btn-nav kiosk-btn"
                onClick={() => {
                  if (pinInput === familySettings.pin) {
                    setIsKiosk(false);
                    setView("parent");
                    setPinInput("");
                    setPinError("");
                  } else {
                    setPinError("Wrong PIN");
                    if (pinErrorTimeoutRef.current) {
                      clearTimeout(pinErrorTimeoutRef.current);
                    }
                    pinErrorTimeoutRef.current = setTimeout(() => setPinError(""), 2000);
                  }
                }}
              >
                Unlock
              </button>
            </>
          ) : (
            <>
              <button className="btn-nav" onClick={() => setIsHelpOpen(true)}>
                📖 Learn
              </button>
              <button className="btn-nav" data-tour="nav-board" onClick={() => setView("kids")}>
                Board
              </button>
              {canManage && (
                <button className="btn-nav" data-tour="nav-parent" onClick={() => setView("parent")}>
                  Parent Hub
                </button>
              )}
              {canManage && (
                <button className="btn-nav" data-tour="nav-settings" onClick={() => setView("settings")}>
                  Settings
                </button>
              )}
              {isAdmin && (
                <button className="btn-nav" onClick={() => setView("admin")}>
                  Admin
                </button>
              )}
              {!isKidAccount && (
                <button className="btn-nav feedback-btn" data-tour="nav-feedback" onClick={onOpenFeedback}>
                  Feedback / Help
                  {feedbackUnreadCount > 0 && (
                    <span className="nav-badge">{feedbackUnreadCount}</span>
                  )}
                </button>
              )}
              {!isKidAccount && (
                <button
                  className="btn-nav kiosk-btn"
                  onClick={() => {
                    setIsKiosk(true);
                    setView("kids");
                  }}
                >
                  Kids Mode
                </button>
              )}
              <button className="btn-nav" onClick={logout}>
                Logout
              </button>
            </>
          )}
        </div>
      </nav>
      {broadcastMessage && (
        <div className="broadcast-banner">
          <span className="broadcast-label">Admin note</span>
          <span>{broadcastMessage}</span>
        </div>
      )}

      <HelpModal
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        onStartTour={onStartTour}
        onStartAdvancedTour={onStartAdvancedTour}
        audience={isKidAccount ? "kid" : "parent"}
      />
    </>
  );
}
